import clsx from 'clsx';
import css from 'dom-helpers/style';
import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Transition, { EXITED, ENTERED, ENTERING, EXITING } from 'react-transition-group/Transition';
import capitalize from '../bootstrap/utils/capitalize';
import createChainedFunction from '../bootstrap/utils/createChainedFunction';
const MARGINS = {
  height: ['marginTop', 'marginBottom'],
  width: ['marginLeft', 'marginRight']
};

// reading a dimension prop will cause the browser to recalculate,
// which will let our animations work
function triggerBrowserReflow(node) {
  node.offsetHeight; // eslint-disable-line no-unused-expressions
}
function getDimensionValue(dimension, elem) {
  const value = elem[`offset${capitalize(dimension)}`];
  const margins = MARGINS[dimension];
  return value + parseInt(css(elem, margins[0]), 10) + parseInt(css(elem, margins[1]), 10);
}
const collapseStyles = {
  [EXITED]: 'collapse',
  [EXITING]: 'collapsing',
  [ENTERING]: 'collapsing',
  [ENTERED]: 'collapse in'
};
function Collapse(_ref) {
  let {
    nodeRef: nodeRefProp,
    in: inProp = false,
    timeout = 300,
    mountOnEnter = false,
    unmountOnExit = false,
    appear = false,
    dimension: dimensionProp = 'height',
    getDimensionValue: getDimensionValueProp = getDimensionValue,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    className,
    children,
    role,
    ...props
  } = _ref;
  // Always use a ref - create one if not provided
  const defaultNodeRef = useRef(null);
  const nodeRef = nodeRefProp || defaultNodeRef;
  const getDimension = useCallback(() => typeof dimensionProp === 'function' ? dimensionProp() : dimensionProp, [dimensionProp]);

  // for testing
  const getScrollDimensionValue = useCallback((elem, dim) => `${elem[`scroll${capitalize(dim)}`]}px`, []);

  /* -- Expanding -- */
  const handleEnter = useCallback(() => {
    const elem = nodeRef.current;
    if (!elem) return;
    const dimension = getDimension();
    elem.style[dimension] = '0';
  }, [getDimension, nodeRef]);
  const handleEntering = useCallback(() => {
    const elem = nodeRef.current;
    if (!elem) return;
    const dimension = getDimension();
    elem.style[dimension] = getScrollDimensionValue(elem, dimension);
  }, [getDimension, getScrollDimensionValue, nodeRef]);
  const handleEntered = useCallback(() => {
    const elem = nodeRef.current;
    if (!elem) return;
    const dimension = getDimension();
    elem.style[dimension] = null;
  }, [getDimension, nodeRef]);

  /* -- Collapsing -- */
  const handleExit = useCallback(() => {
    const elem = nodeRef.current;
    if (!elem) return;
    const dimension = getDimension();
    elem.style[dimension] = `${getDimensionValueProp(dimension, elem)}px`;
    triggerBrowserReflow(elem);
  }, [getDimension, getDimensionValueProp, nodeRef]);
  const handleExiting = useCallback(() => {
    const elem = nodeRef.current;
    if (!elem) return;
    const dimension = getDimension();
    elem.style[dimension] = '0';
  }, [getDimension, nodeRef]);
  const combinedHandleEnter = createChainedFunction(handleEnter, onEnter);
  const combinedHandleEntering = createChainedFunction(handleEntering, onEntering);
  const combinedHandleEntered = createChainedFunction(handleEntered, onEntered);
  const combinedHandleExit = createChainedFunction(handleExit, onExit);
  const combinedHandleExiting = createChainedFunction(handleExiting, onExiting);
  return /*#__PURE__*/React.createElement(Transition, Object.assign({}, props, {
    in: inProp,
    timeout: timeout,
    mountOnEnter: mountOnEnter,
    unmountOnExit: unmountOnExit,
    appear: appear,
    nodeRef: nodeRef,
    "aria-expanded": role ? inProp : null,
    onEnter: combinedHandleEnter,
    onEntering: combinedHandleEntering,
    onEntered: combinedHandleEntered,
    onExit: combinedHandleExit,
    onExiting: combinedHandleExiting
  }), (state, innerProps) => /*#__PURE__*/React.cloneElement(children, {
    ...innerProps,
    ref: nodeRef,
    className: clsx(className, children.props.className, collapseStyles[state], getDimension() === 'width' && 'width')
  }));
}
export default Collapse;